<!-- ═══ STOCKFLOW — TOPBAR / MEGA-MENU ═══ -->

<header class="header">
  <div class="h-top">

    <!-- Logo -->
    <div class="logo" (click)="navigate('/dashboard')">
      <div class="logo-ico">S</div>
      <div class="logo-name">Stock<i>Flow</i></div>
    </div>

    <!-- ═══ HORIZONTAL NAV (bar mode) ═══ -->
    <nav class="nav" [class.hidden]="navMode === 'dock'">
      @for (section of menuService.menuSections(); track section.label) {
        @if (section.routerLink) {
          <!-- Direct link (no dropdown) -->
          <div class="ni ni-link" (click)="navigate(section.routerLink); $event.stopPropagation()">
            {{ section.label | translate }}
            @if (section.badge) {
              <span class="nb" [ngClass]="section.badgeClass || 'nb-i'">{{ section.badge }}</span>
            }
          </div>
        } @else {
          <!-- Dropdown section -->
          <div class="ni"
               [class.open]="openDropdown === section.label"
               (click)="toggleDropdown(section.label); $event.stopPropagation()">
            {{ section.label | translate }}
            @if (section.badge) {
              <span class="nb" [ngClass]="section.badgeClass || 'nb-i'">{{ section.badge }}</span>
            }
            <svg class="ch" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>

            <!-- Mega dropdown -->
            <div class="mega" [style.min-width]="section.items.length > 4 ? '280px' : '260px'">
              @for (item of section.items; track $index) {
                @if (item.separator) {
                  <div class="ms"></div>
                } @else {
                  <div class="mi" (click)="navigate(item.routerLink!); $event.stopPropagation()">
                    <div class="mi-ic" [ngClass]="item.gradient || 'g-indigo'"
                         [innerHTML]="svgFor(item.svgIcon, 15)"></div>
                    <div>
                      <div class="mi-n">{{ item.label | translate }}</div>
                      @if (item.description) {
                        <div class="mi-d">{{ item.description | translate }}</div>
                      }
                    </div>
                    @if (item.badge) {
                      <div class="mi-b" [ngClass]="item.badgeSeverity === 'danger' ? 'mr' : item.badgeSeverity === 'success' ? 'mg' : 'mb'">{{ item.badge }}</div>
                    }
                  </div>
                }
              }
            </div>
          </div>
        }
      }
    </nav>

    <!-- ═══ RIGHT SECTION ═══ -->
    <div class="hr">

      <!-- Mode Switch -->
      <div class="mode-sw">
        <div class="mode-btn" [class.active]="navMode === 'bar'" (click)="setNavMode('bar')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          {{ 'MENU.BAR' | translate }}
        </div>
        <div class="mode-btn" [class.active]="navMode === 'dock'" (click)="setNavMode('dock')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="17" width="18" height="4" rx="1"/><rect x="5" y="6" width="4" height="4" rx="1"/><rect x="10" y="6" width="4" height="4" rx="1"/><rect x="15" y="6" width="4" height="4" rx="1"/></svg>
          {{ 'MENU.DOCK' | translate }}
        </div>
      </div>

      <!-- AI Assistant -->
      @if (enableAiModule) {
        <div class="ib ai-btn" (click)="navigate('/ai')" style="position:relative; cursor:pointer" [title]="'AI.TITLE' | translate">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1.27A7 7 0 0 1 14 22h-4a7 7 0 0 1-6.73-3H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"/>
            <circle cx="9" cy="15" r="1"/><circle cx="15" cy="15" r="1"/>
          </svg>
        </div>
      }

      <!-- Sync (Tauri only) -->
      @if (isTauri) {
        <div class="si" (click)="toggleSyncPanel(); $event.stopPropagation()" style="cursor:pointer">
          <div class="sd" [class.sd-yellow]="offlineState.hydrationStatus() === 'hydrating' || offlineState.pendingCount() > 0"
               [class.sd-red]="!offlineState.online() || offlineState.hydrationStatus() === 'error'"></div>
          <span>{{ offlineState.online() ? ('OFFLINE.STATE_SYNC' | translate) : ('OFFLINE.STATE_OFFLINE' | translate) }}</span>
          @if (offlineState.pendingCount() > 0) {
            <span class="si-badge">{{ offlineState.pendingCount() }}</span>
          }
        </div>
      }

      <!-- Session ouverte par PIN sans reseau -->
      <app-session-hors-ligne-pastille />

      <!-- Warehouse Selector -->
      @if (availableWarehouses.length > 0) {
        <div class="ib warehouse-selector" (click)="toggleDropdown('warehouse'); $event.stopPropagation()" style="position:relative; display:flex; align-items:center; gap:4px; width:auto; padding:0 8px;">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          <span class="warehouse-label">{{ warehouseService.currentWarehouse()?.name || ('WAREHOUSE.ALL_WAREHOUSES' | translate) }}</span>
          <svg class="ch" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="width:12px;height:12px;opacity:.4"><polyline points="6 9 12 15 18 9"/></svg>
          <div class="dd right" [class.open]="openDropdown === 'warehouse'" style="width:240px; max-height:320px; overflow:hidden; display:flex; flex-direction:column">
            <div class="dd-head">{{ 'WAREHOUSE.CURRENT_WAREHOUSE' | translate }}</div>
            <!-- All warehouses option -->
            <div class="dd-item" [class.active]="!warehouseService.currentWarehouseId()"
                 (click)="clearWarehouseSelection(); $event.stopPropagation()">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
              <div>{{ 'WAREHOUSE.ALL_WAREHOUSES' | translate }}</div>
              @if (!warehouseService.currentWarehouseId()) {
                <div class="check">&#10003;</div>
              }
            </div>
            <div class="dd-sep"></div>
            <div style="overflow-y:auto; max-height:220px">
              @for (wh of availableWarehouses; track wh.id) {
                <div class="dd-item" [class.active]="warehouseService.currentWarehouseId() === wh.id"
                     (click)="selectWarehouse(wh); $event.stopPropagation()">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                  <div>
                    <div>{{ wh.name }}</div>
                    @if (wh.location || wh.city) {
                      <div class="dd-sub">{{ wh.location || wh.city }}</div>
                    }
                  </div>
                  @if (warehouseService.currentWarehouseId() === wh.id) {
                    <div class="check">&#10003;</div>
                  }
                </div>
              }
            </div>
          </div>
        </div>
      }

      <!-- Language -->
      <div class="ib" (click)="toggleDropdown('lang'); $event.stopPropagation()" style="position:relative">
        <span class="lang-label">{{ currentLanguage?.flag || 'FR' }}</span>
        <div class="dd right" [class.open]="openDropdown === 'lang'" style="width:200px">
          <div class="dd-head">{{ 'MENU.LANGUAGE' | translate }}</div>
          @for (lang of languages; track lang.code) {
            <div class="dd-item" [class.active]="lang.code === currentLang"
                 (click)="setLang(lang.code); $event.stopPropagation()">
              <div class="flag" [ngClass]="'fl-' + lang.code">{{ lang.flag }}</div>
              <div>{{ lang.label }}</div>
              <div class="check">&#10003;</div>
            </div>
          }
        </div>
      </div>

      <!-- Notifications -->
      <div class="ib" (click)="navigate('/stock-alerts')" style="position:relative; cursor:pointer" title="{{ 'STOCK_ALERTS.TITLE' | translate }}">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
        @if (stockAlertCount() > 0) {
          <span class="notif-badge">{{ stockAlertCount() }}</span>
        }
      </div>

      <!-- Theme -->
      <div class="ib" (click)="toggleDropdown('theme'); $event.stopPropagation()" style="position:relative">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
        <div class="dd right" [class.open]="openDropdown === 'theme'" style="width:180px">
          @for (theme of themes; track theme.id) {
            <div class="to" [class.act]="theme.id === activeTheme"
                 (click)="setTheme(theme.id); $event.stopPropagation()">
              <div class="tw" [ngClass]="'sw-' + theme.id[0]">
                <div class="tc">&#10003;</div>
              </div>
              {{ theme.label | translate }}
            </div>
          }
        </div>
      </div>

      <!-- Verrouiller le poste -->
      <div class="ib" role="button" tabindex="0" style="cursor:pointer" (click)="verrouiller(); $event.stopPropagation()" (keydown.enter)="verrouiller()"
           [attr.aria-label]="'VERROU.BUTTON' | translate" title="{{ 'VERROU.BUTTON' | translate }} (Ctrl+Alt+L)"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></div>

      <!-- Company Switcher (Super Admin only) -->
      @if (isAdmin && allCompanies.length > 0) {
        <div class="ib company-switcher" (click)="toggleDropdown('company-switch'); $event.stopPropagation()" style="position:relative">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
          <span class="company-label">{{ selectedCompany || ('COMPANY.TITLE' | translate) }}</span>
          <div class="dd right" [class.open]="openDropdown === 'company-switch'" style="width:280px; max-height:360px; overflow:hidden; display:flex; flex-direction:column">
            <div class="dd-head">{{ 'MENU.SWITCH_COMPANY' | translate }}</div>
            @if (allCompanies.length > 5) {
              <div style="padding:6px 10px">
                <input type="text" [(ngModel)]="companySwitchSearch" (input)="filterSwitchCompanies()"
                  [placeholder]="'COMMON.SEARCH_PLACEHOLDER' | translate" (click)="$event.stopPropagation()"
                  style="width:100%; padding:6px 10px; border:1px solid #e2e8f0; border-radius:6px; font-size:0.85rem; outline:none">
              </div>
            }
            <div style="overflow-y:auto; max-height:260px">
              @for (company of filteredSwitchCompanies; track company.id) {
                <div class="dd-item" [class.active]="selectedCompany === company.name"
                     (click)="selectSwitchCompany(company); $event.stopPropagation()">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                  <div>
                    <div>{{ company.name }}</div>
                    <div class="dd-sub">{{ company.sigle || '' }} {{ company.planName ? '· ' + company.planName : '' }}</div>
                  </div>
                  @if (selectedCompany === company.name) {
                    <div class="check">&#10003;</div>
                  }
                </div>
              }
              @if (filteredSwitchCompanies.length === 0) {
                <div class="dd-item" style="opacity:0.5; cursor:default">{{ 'MENU.NO_COMPANY' | translate }}</div>
              }
            </div>
          </div>
        </div>
      }

      <!-- Profile -->
      <div class="pf" [class.open]="openDropdown === 'profile'"
           (click)="toggleDropdown('profile'); $event.stopPropagation()">
        @if (currentUser?.avatarUrl) {
          <img class="pf-img" [src]="currentUser!.avatarUrl" [alt]="'USER.AVATAR' | translate">
        } @else {
          <div class="pf-a">{{ getUserInitials() }}</div>
        }
        <div>
          <div class="pf-n">{{ currentUser?.name || ('USER.TITLE' | translate) }}</div>
          <div class="pf-r">{{ selectedCompany || 'StockFlow' }}</div>
        </div>
        <svg class="ch2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>

        <div class="dd right" [class.open]="openDropdown === 'profile'" style="width:240px">
          <!-- Profile header -->
          <div class="profile-header">
            @if (currentUser?.avatarUrl) {
              <img class="pf-img pf-img-lg" [src]="currentUser!.avatarUrl" [alt]="'USER.AVATAR' | translate">
            } @else {
              <div class="pf-a pf-a-lg">{{ getUserInitials() }}</div>
            }
            <div>
              <div class="ph-name">{{ currentUser?.name || ('USER.TITLE' | translate) }}</div>
              <div class="ph-email">{{ currentUser?.email || '' }}</div>
              @if (currentPlanName) {
                <div style="margin-top:3px"><span class="pb" style="font-size:9px;padding:2px 8px">{{ currentPlanName }}</span></div>
              }
            </div>
          </div>

          <div class="dd-item" (click)="goToProfile(); $event.stopPropagation()">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            <div>
              <div>{{ 'MENU.EDIT_PROFILE' | translate }}</div>
              <div class="dd-sub">{{ 'MENU.NAME_EMAIL_PHOTO' | translate }}</div>
            </div>
          </div>
          <div class="dd-item" (click)="goToCompanyProfile(); $event.stopPropagation()">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
            <div>
              <div>{{ 'MENU.COMPANY_PROFILE' | translate }}</div>
              <div class="dd-sub">{{ 'MENU.LOGO_ADDRESS' | translate }}</div>
            </div>
          </div>
          <div class="dd-sep"></div>
          <div class="dd-item" (click)="openChangePassword(); $event.stopPropagation()">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            <div>
              <div>{{ 'MENU.CHANGE_PASSWORD' | translate }}</div>
              <div class="dd-sub">{{ 'MENU.ACCOUNT_SECURITY' | translate }}</div>
            </div>
          </div>
          <div class="dd-sep"></div>
          <div class="dd-item" (click)="createNewCompany(); $event.stopPropagation()">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
            <div>{{ 'MENU.NEW_COMPANY' | translate }}</div>
          </div>
          @if (isAdmin) {
            <!-- Super admin: navigate to full company selection page -->
            <div class="dd-item" (click)="switchCompany(); $event.stopPropagation()">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
              <div>{{ 'MENU.SWITCH_COMPANY' | translate }}</div>
            </div>
          } @else if (showUserCompanySwitcher) {
            <!-- Regular user with multiple tenants: inline list -->
            <div class="dd-sub-head" style="padding:6px 14px; font-size:0.7rem; color:#9ca3af; text-transform:uppercase; letter-spacing:0.5px">
              {{ 'MENU.SWITCH_COMPANY' | translate }}
            </div>
            @for (uc of userCompanies; track uc.companyId) {
              <div class="dd-item" (click)="selectUserCompany(uc); $event.stopPropagation()">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                <div>
                  <div>{{ uc.name }}</div>
                  @if (uc.sigle) {
                    <div class="dd-sub">{{ uc.sigle }}</div>
                  }
                </div>
              </div>
            }
          }

          @if (pwa.proposable()) {
            <div class="dd-item" (click)="installApp(); $event.stopPropagation()">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 15V3"/><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="m7 10 5 5 5-5"/></svg>
              <div><div>{{ 'PWA.INSTALL_MENU' | translate }}</div><div class="dd-sub">{{ 'PWA.INSTALL_MENU_SUB' | translate }}</div></div>
            </div>
          }
          <div class="dd-sep"></div>
          <div class="dd-item danger" (click)="logout(); $event.stopPropagation()">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            <div>{{ 'MENU.LOGOUT' | translate }}</div>
          </div>
        </div>
      </div>

    </div><!-- /hr -->
  </div><!-- /h-top -->
</header>

<!-- ═══ PROFILE MODAL ═══ -->
<app-topbar-profile-modal [showProfileModal]="showProfileModal" [profileAvatarUrl]="profileAvatarUrl" [currentUser]="currentUser"
  [selectedCompany]="selectedCompany" [currentLanguage]="currentLanguage" [languages]="languages" [currentLang]="currentLang"
  [activeTheme]="activeTheme" [activeThemeLabel]="activeThemeLabel" [themes]="themes"
  [profileContacts]="profileContacts"
  [parentalLinks]="parentalLinks" [profileSaving]="profileSaving" [port]="this"
  [(profileEmail)]="profileEmail" [(profilePhone)]="profilePhone" [(pmLangOpen)]="pmLangOpen" [(pmThemeOpen)]="pmThemeOpen"
  (closed)="closeProfileModal()" (photoSelected)="onProfilePhotoSelected($event)"
  (languageSelected)="setLang($event)" (themeSelected)="setTheme($event)"
  (warehouseCleared)="clearWarehouseSelection()" (warehouseSelected)="selectWarehouse($event)"
  (contactAdded)="addContact()" (contactRemoved)="removeContact($event)" (saved)="saveProfileModal()" />

<!-- ═══ CHANGE PASSWORD MODAL ═══ -->
<app-topbar-password-modal [showPasswordModal]="showPasswordModal" [currentUser]="currentUser" [pwdError]="pwdError"
  [pwdSaving]="pwdSaving" [(currentPwd)]="currentPwd" [(newPwd)]="newPwd" [(confirmPwd)]="confirmPwd"
  [(showCurrentPwd)]="showCurrentPwd" [(showNewPwd)]="showNewPwd" [(showConfirmPwd)]="showConfirmPwd"
  (closed)="closePasswordModal()" (submitted)="submitChangePassword()" />

<!-- ═══ PIN Change Dialog ═══ -->
@if (showPinChangeDialog) {
  <app-topbar-pin-change-dialog [pinChangeMode]="pinChangeMode" [pinChangeStep]="pinChangeStep"
    [pinChangeDigits]="pinChangeDigits" [pinChangeError]="pinChangeError" (closed)="closePinChangeDialog()"
    (digitEntered)="pinChangeEnterDigit($event)" (digitDeleted)="pinChangeDeleteDigit()" />
}

<!-- ═══ OVERLAY (dock window) ═══ -->
<app-topbar-dock [showWindow]="showWindow" [windowTitle]="windowTitle" [windowItems]="windowItems" [navMode]="navMode"
  [menuSections]="menuService.menuSections()" [dashboardDockSvg]="dashboardDockSvg" [port]="this"
  (windowClosed)="closeWindow()" (windowItemClicked)="windowItemClick($event)"
  (dockWindowOpened)="openDockWindow($event.labelKey, $event.key)" (navigated)="navigate($event)" />

<!-- ═══ SYNC STATUS PANEL ═══ -->
@if (isTauri) {
  <app-topbar-sync-panel [showSyncPanel]="showSyncPanel" [offlineState]="offlineState" [pendingRequests]="pendingRequests"
    [syncLogs]="syncLogs" (closed)="closeSyncPanel()" />
}

<!-- PIN setup/modify dialog -->
<app-pin-setup-dialog
  [(visible)]="showPinDialog"
  [closable]="true"
  [mode]="pinDialogMode"
  (pinSaved)="onPinSaved()"
/>

<app-dialogue-installation-ios [(visible)]="showInstallDialog" />
